import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Plus,
	Search,
	Download,
	Building2,
	MoreVertical,
	Edit2,
	Trash2,
} from 'lucide-react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'

import { cn, downloadCSV } from '../../lib/utils'
import { useAuth } from '../../providers/AuthProvider'
import { useToast } from '../../components/ui/Toast'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import { PageHeader } from '../../components/ui/PageHeader'
import { Card, CardContent } from '../../components/ui/Card'
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from '../../components/ui/Table'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '../../components/ui/Dialog'
import Checkbox from '../../components/ui/Checkbox'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import {
	useOutlets,
	useCreateOutlet,
	useUpdateOutlet,
	useDeleteOutlet,
	type Outlet,
	type CreateOutletData,
	type UpdateOutletData,
} from '../../hooks/useOutlets'

interface OutletFormData {
	code: string
	name: string
	nameAr: string
	address: string
	phone: string
	email: string
	isStore: boolean
	isMainStore: boolean
	isActive: boolean
}

const initialFormData: OutletFormData = {
	code: '',
	name: '',
	nameAr: '',
	address: '',
	phone: '',
	email: '',
	isStore: false,
	isMainStore: false,
	isActive: true,
}

export default function OutletsPage() {
	const { t, i18n } = useTranslation()
	const { hasPermission } = useAuth()
	const { toast } = useToast()

	const [page, setPage] = useState(1)
	const [searchInputValue, setSearchInputValue] = useState('')
	const [searchTerm, setSearchTerm] = useState('')
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [selectedOutlet, setSelectedOutlet] = useState<Outlet | null>(null)
	const [formData, setFormData] = useState<OutletFormData>(initialFormData)
	const [formErrors, setFormErrors] = useState<Record<string, string>>({})

	const isRtl = i18n.dir() === 'rtl'

	const {
		data: outletsData,
		isLoading,
		error,
	} = useOutlets({
		page,
		limit: 20,
		search: searchTerm || undefined,
	})

	const outlets = outletsData?.data ?? []
	const pagination = outletsData?.pagination

	const createOutletMutation = useCreateOutlet()
	const updateOutletMutation = useUpdateOutlet()
	const deleteOutletMutation = useDeleteOutlet()

	const validateForm = useCallback(
		(data: OutletFormData): Record<string, string> => {
			const errors: Record<string, string> = {}

			if (!data.code.trim()) {
				errors.code = t('outlets.validation.codeRequired', 'Outlet code is required')
			}

			if (!data.name.trim()) {
				errors.name = t('outlets.validation.nameRequired', 'Name is required')
			}

			return errors
		},
		[t]
	)

	const handleCloseCreateDialog = () => {
		setIsCreateDialogOpen(false)
		setFormData(initialFormData)
		setFormErrors({})
	}

	const handleCloseEditDialog = () => {
		setIsEditDialogOpen(false)
		setSelectedOutlet(null)
		setFormData(initialFormData)
		setFormErrors({})
	}

	const handleCloseDeleteDialog = () => {
		setIsDeleteDialogOpen(false)
		setSelectedOutlet(null)
	}

	const handleOpenEditDialog = (outlet: Outlet) => {
		setSelectedOutlet(outlet)
		setFormData({
			code: outlet.code,
			name: outlet.name,
			nameAr: outlet.nameAr || '',
			address: outlet.address || '',
			phone: outlet.phone || '',
			email: outlet.email || '',
			isStore: outlet.isStore,
			isMainStore: outlet.isMainStore,
			isActive: outlet.isActive,
		})
		setIsEditDialogOpen(true)
	}

	const handleOpenDeleteDialog = (outlet: Outlet) => {
		setSelectedOutlet(outlet)
		setIsDeleteDialogOpen(true)
	}

	const handleCreateOutlet = async () => {
		const errors = validateForm(formData)
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors)
			return
		}

		const createData: CreateOutletData = {
			code: formData.code,
			name: formData.name,
			nameAr: formData.nameAr || undefined,
			address: formData.address || undefined,
			phone: formData.phone || undefined,
			email: formData.email || undefined,
			isStore: formData.isStore,
			isMainStore: formData.isMainStore,
			isActive: formData.isActive,
		}

		try {
			await createOutletMutation.mutateAsync(createData)
			toast.success(t('outlets.messages.createSuccess', 'Outlet created successfully'))
			handleCloseCreateDialog()
		} catch (error) {
			const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
			toast.error(t('common.error', 'Error'), message)
		}
	}

	const handleUpdateOutlet = async () => {
		if (!selectedOutlet) return

		const errors = validateForm(formData)
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors)
			return
		}

		const updateData: UpdateOutletData = {
			name: formData.name,
			nameAr: formData.nameAr || undefined,
			address: formData.address || undefined,
			phone: formData.phone || undefined,
			email: formData.email || undefined,
			isStore: formData.isStore,
			isMainStore: formData.isMainStore,
			isActive: formData.isActive,
		}

		try {
			await updateOutletMutation.mutateAsync({
				id: selectedOutlet.id || selectedOutlet._id || '',
				data: updateData,
			})
			toast.success(t('outlets.messages.updateSuccess', 'Outlet updated successfully'))
			handleCloseEditDialog()
		} catch (error) {
			const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
			toast.error(t('common.error', 'Error'), message)
		}
	}

	const handleDeleteOutlet = async () => {
		if (!selectedOutlet) return

		try {
			await deleteOutletMutation.mutateAsync(selectedOutlet.id || selectedOutlet._id || '')
			toast.success(t('outlets.messages.deleteSuccess', 'Outlet deleted successfully'))
			handleCloseDeleteDialog()
		} catch (error) {
			const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
			toast.error(t('common.error', 'Error'), message)
		}
	}

	const handleExportCSV = useCallback(() => {
		const exportData = outlets.map((outlet) => ({
			[t('outlets.fields.code', 'Code')]: outlet.code,
			[t('outlets.fields.name', 'Name')]: outlet.name,
			[t('outlets.fields.nameAr', 'Arabic Name')]: outlet.nameAr || '',
			[t('outlets.fields.address', 'Address')]: outlet.address || '',
			[t('outlets.fields.phone', 'Phone')]: outlet.phone || '',
			[t('outlets.fields.email', 'Email')]: outlet.email || '',
			[t('outlets.fields.isStore', 'Is Store')]: outlet.isStore ? t('common.yes', 'Yes') : t('common.no', 'No'),
			[t('outlets.fields.status', 'Status')]: outlet.isActive
				? t('common.active', 'Active')
				: t('common.inactive', 'Inactive'),
		}))

		downloadCSV(exportData, `outlets_${new Date().toISOString().split('T')[0]}`)
	}, [outlets, t])

	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const handleSearch = useCallback((value: string) => {
		setSearchInputValue(value)
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}
		searchTimeoutRef.current = setTimeout(() => {
			setSearchTerm(value)
			setPage(1)
		}, 300)
	}, [])

	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
				<LoadingSpinner size="lg" />
			</div>
		)
	}

	if (error) {
		const errorMessage = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
		return (
			<div className="flex flex-col items-center justify-center min-h-[400px] text-center" role="alert">
				<Building2 className="h-12 w-12 text-gray-400 mb-4" />
				<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
					{t('common.error', 'Error')}
				</h3>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{errorMessage}</p>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title={t('outlets.title', 'Outlets')}
				subtitle={t('outlets.subtitle', 'Manage outlets and locations')}
				actions={
					hasPermission('outlets:manage') && (
						<Button
							onClick={() => setIsCreateDialogOpen(true)}
							leftIcon={<Plus className="h-4 w-4" />}
						>
							{t('outlets.addOutlet', 'Add Outlet')}
						</Button>
					)
				}
			/>

			<Card>
				<CardContent className="p-0">
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
							<div className="relative flex-1 max-w-md">
								<Input
									type="text"
									placeholder={t('outlets.searchPlaceholder', 'Search outlets...')}
									value={searchInputValue}
									onChange={(e) => handleSearch(e.target.value)}
									leftIcon={<Search className="h-4 w-4" />}
								/>
							</div>
							<Button variant="outline" onClick={handleExportCSV} leftIcon={<Download className="h-4 w-4" />}>
								{t('common.exportCSV', 'Export CSV')}
							</Button>
						</div>
					</div>

					{outlets.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-center">
							<Building2 className="h-12 w-12 text-gray-400 mb-4" />
							<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
								{t('outlets.noOutlets', 'No outlets found')}
							</h3>
							<p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
								{t('outlets.noOutletsDescription', 'Get started by adding your first outlet.')}
							</p>
							{hasPermission('outlets:manage') && (
								<Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
									{t('outlets.addOutlet', 'Add Outlet')}
								</Button>
							)}
						</div>
					) : (
						<>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t('outlets.fields.code', 'Code')}</TableHead>
										<TableHead>{t('outlets.fields.name', 'Name')}</TableHead>
										<TableHead>{t('outlets.fields.address', 'Address')}</TableHead>
										<TableHead>{t('outlets.fields.phone', 'Phone')}</TableHead>
										<TableHead>{t('outlets.fields.isStore', 'Store')}</TableHead>
										<TableHead>{t('outlets.fields.status', 'Status')}</TableHead>
										<TableHead className="ltr:text-right rtl:text-left">
											{t('common.actions', 'Actions')}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{outlets.map((outlet) => (
										<TableRow key={outlet.id}>
											<TableCell className="font-medium font-mono">{outlet.code}</TableCell>
											<TableCell>
												<div>
													<div className="font-medium">{i18n.language === 'ar' ? outlet.nameAr || outlet.name : outlet.name}</div>
													{outlet.nameAr && outlet.nameAr !== outlet.name && (
														<div className="text-sm text-gray-500">{outlet.name}</div>
													)}
												</div>
											</TableCell>
											<TableCell>{outlet.address || '-'}</TableCell>
											<TableCell>{outlet.phone || '-'}</TableCell>
											<TableCell>
												{outlet.isStore ? (
													<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
														{t('outlets.store', 'Store')}
													</span>
												) : (
													<span className="text-gray-400">-</span>
												)}
											</TableCell>
											<TableCell>
												<span
													className={cn(
														'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
														outlet.isActive
															? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
															: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
													)}
												>
													{outlet.isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
												</span>
											</TableCell>
											<TableCell className="ltr:text-right rtl:text-left">
												<Menu as="div" className="relative inline-block text-left">
													<MenuButton
														as={Button}
														variant="ghost"
														size="icon"
														className="h-8 w-8"
													>
														<span className="sr-only">{t('common.actions', 'Actions')}</span>
														<MoreVertical className="h-4 w-4" />
													</MenuButton>
													<MenuItems
														className={cn(
															'absolute z-10 mt-2 w-48 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
															isRtl ? 'left-0' : 'right-0'
														)}
													>
														<div className="py-1">
															{hasPermission('outlets:manage') && (
																<MenuItem>
																	{({ focus }) => (
																		<button
																			onClick={() => handleOpenEditDialog(outlet)}
																			className={cn(
																				'w-full flex items-center gap-2 px-4 py-2 text-sm',
																				focus
																					? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
																					: 'text-gray-700 dark:text-gray-300'
																			)}
																		>
																			<Edit2 className="h-4 w-4" />
																			{t('outlets.editOutlet', 'Edit Outlet')}
																		</button>
																	)}
																</MenuItem>
															)}
															{hasPermission('outlets:manage') && (
																<MenuItem>
																	{({ focus }) => (
																		<button
																			onClick={() => handleOpenDeleteDialog(outlet)}
																			className={cn(
																				'w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400',
																				focus && 'bg-red-50 dark:bg-red-900/20'
																			)}
																		>
																			<Trash2 className="h-4 w-4" />
																			{t('outlets.deleteOutlet', 'Delete Outlet')}
																		</button>
																	)}
																</MenuItem>
															)}
														</div>
													</MenuItems>
												</Menu>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>

							{pagination && pagination.totalPages > 1 && (
								<nav
									className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700"
									aria-label={t('pagination.navigation', 'Pagination navigation')}
								>
									<div className="text-sm text-gray-500 dark:text-gray-400">
										{t('common.showing', 'Showing')} {(page - 1) * 20 + 1}-
										{Math.min(page * 20, pagination.total)} {t('common.of', 'of')} {pagination.total}
									</div>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={page === 1}
										>
											{t('common.previous', 'Previous')}
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
											disabled={page === pagination.totalPages}
										>
											{t('common.next', 'Next')}
										</Button>
									</div>
								</nav>
							)}
						</>
					)}
				</CardContent>
			</Card>

			{/* Create Outlet Dialog */}
			<Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog}>
				<DialogHeader onClose={handleCloseCreateDialog}>
					<DialogTitle>{t('outlets.addOutlet', 'Add Outlet')}</DialogTitle>
					<DialogDescription>
						{t('outlets.addOutletDescription', 'Add a new outlet or location')}
					</DialogDescription>
				</DialogHeader>
				<DialogContent>
					<div className="space-y-4 max-h-[60vh] overflow-y-auto">
						<Input
							label={t('outlets.fields.code', 'Code')}
							name="code"
							value={formData.code}
							onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
							error={formErrors.code}
							isRequired
						/>
						<Input
							label={t('outlets.fields.name', 'Name (English)')}
							name="name"
							value={formData.name}
							onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
							error={formErrors.name}
							isRequired
						/>
						<Input
							label={t('outlets.fields.nameAr', 'Name (Arabic)')}
							name="nameAr"
							value={formData.nameAr}
							onChange={(e) => setFormData((prev) => ({ ...prev, nameAr: e.target.value }))}
							dir="rtl"
						/>
						<Input
							label={t('outlets.fields.address', 'Address')}
							name="address"
							value={formData.address}
							onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
						/>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<Input
								label={t('outlets.fields.phone', 'Phone')}
								name="phone"
								value={formData.phone}
								onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
							/>
							<Input
								label={t('outlets.fields.email', 'Email')}
								name="email"
								type="email"
								value={formData.email}
								onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
							/>
						</div>
						<Checkbox
							id="isStore"
							label={t('outlets.fields.isStore', 'This is a store')}
							checked={formData.isStore}
							onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isStore: checked }))}
						/>
						{formData.isStore && (
							<Checkbox
								id="isMainStore"
								label={t('outlets.fields.isMainStore', 'Main store')}
								checked={formData.isMainStore}
								onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isMainStore: checked }))}
							/>
						)}
					</div>
				</DialogContent>
				<DialogFooter>
					<Button variant="outline" onClick={handleCloseCreateDialog}>
						{t('common.cancel', 'Cancel')}
					</Button>
					<Button onClick={handleCreateOutlet} isLoading={createOutletMutation.isPending}>
						{t('common.save', 'Save')}
					</Button>
				</DialogFooter>
			</Dialog>

			{/* Edit Outlet Dialog - Similar structure */}
			<Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog}>
				<DialogHeader onClose={handleCloseEditDialog}>
					<DialogTitle>{t('outlets.editOutlet', 'Edit Outlet')}</DialogTitle>
					<DialogDescription>{selectedOutlet?.code}</DialogDescription>
				</DialogHeader>
				<DialogContent>
					<div className="space-y-4 max-h-[60vh] overflow-y-auto">
						<Input
							label={t('outlets.fields.code', 'Code')}
							name="code"
							value={formData.code}
							disabled
						/>
						<Input
							label={t('outlets.fields.name', 'Name (English)')}
							name="name"
							value={formData.name}
							onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
							error={formErrors.name}
							isRequired
						/>
						<Input
							label={t('outlets.fields.nameAr', 'Name (Arabic)')}
							name="nameAr"
							value={formData.nameAr}
							onChange={(e) => setFormData((prev) => ({ ...prev, nameAr: e.target.value }))}
							dir="rtl"
						/>
						<Input
							label={t('outlets.fields.address', 'Address')}
							name="address"
							value={formData.address}
							onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
						/>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<Input
								label={t('outlets.fields.phone', 'Phone')}
								name="phone"
								value={formData.phone}
								onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
							/>
							<Input
								label={t('outlets.fields.email', 'Email')}
								name="email"
								type="email"
								value={formData.email}
								onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
							/>
						</div>
						<Checkbox
							id="edit-isStore"
							label={t('outlets.fields.isStore', 'This is a store')}
							checked={formData.isStore}
							onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isStore: checked }))}
						/>
						{formData.isStore && (
							<Checkbox
								id="edit-isMainStore"
								label={t('outlets.fields.isMainStore', 'Main store')}
								checked={formData.isMainStore}
								onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isMainStore: checked }))}
							/>
						)}
						<Checkbox
							id="edit-isActive"
							label={t('outlets.fields.isActive', 'Outlet is active')}
							checked={formData.isActive}
							onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isActive: checked }))}
						/>
					</div>
				</DialogContent>
				<DialogFooter>
					<Button variant="outline" onClick={handleCloseEditDialog}>
						{t('common.cancel', 'Cancel')}
					</Button>
					<Button onClick={handleUpdateOutlet} isLoading={updateOutletMutation.isPending}>
						{t('common.save', 'Save')}
					</Button>
				</DialogFooter>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
				<DialogHeader onClose={handleCloseDeleteDialog}>
					<DialogTitle>{t('outlets.deleteOutlet', 'Delete Outlet')}</DialogTitle>
					<DialogDescription>
						{t('outlets.confirmDelete', 'Are you sure you want to delete this outlet?')}
					</DialogDescription>
				</DialogHeader>
				<DialogContent>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						{t('outlets.confirmDeleteDescription', 'This action will deactivate the outlet. It can be restored later.')}
					</p>
					{selectedOutlet && (
						<div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
							<p className="font-medium text-gray-900 dark:text-gray-100">{selectedOutlet.code}</p>
							<p className="text-sm text-gray-500 dark:text-gray-400">{selectedOutlet.name}</p>
						</div>
					)}
				</DialogContent>
				<DialogFooter>
					<Button variant="outline" onClick={handleCloseDeleteDialog}>
						{t('common.cancel', 'Cancel')}
					</Button>
					<Button
						variant="destructive"
						onClick={handleDeleteOutlet}
						isLoading={deleteOutletMutation.isPending}
					>
						{t('common.delete', 'Delete')}
					</Button>
				</DialogFooter>
			</Dialog>
		</div>
	)
}
